import React, { cloneElement, FC, ReactElement, ReactNode, useContext, useEffect, useState } from 'react';
import { Card, Spinner } from 'flowbite-react';
import userIcon from '../../assets/userIcon.png';
import { FaArrowLeft, FaCheck } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import * as styles from './kyc.styles';
import PrivateLayout from '../PrivateLayout/PrivateLayout';
import client from '../../api/client';
import showToast from '../../components/CustomToast/toastHelper';
import { UserContext } from '../../contexts/UserContext';

interface KycProps {
    children: ReactNode;
}

interface OnboardingPercentages {
    personalInformation: number;
    businessInformation: number;
    businessCompliance: number;
    bankInformation: number;
    termsAndCondition: number;
}

const Kyc: FC<KycProps> = ({ children }) => {
    const { userDetails } = useContext(UserContext) ?? {}

    const [selectedCard, setSelectedCard] = useState<string | null>('Personal Info');
    const [onboardingPercentages, setOnboardingPercentages] = useState<OnboardingPercentages>({
        personalInformation: 0,
        businessInformation: 0,
        businessCompliance: 0,
        bankInformation: 0,
        termsAndCondition: 0,
    });
    const [businessType, setBusinessType] = useState<string | null>(userDetails?.activeSubsidiary?.businessType);
    const [loading, setLoading] = useState<boolean>(true)
    const [showGoLive, setShowGoLive] = useState<boolean>(false)
    const navigate = useNavigate();

    const handleCardClick = (path: string, cardName: string) => {
        localStorage.setItem('activeCard', cardName)
        setSelectedCard(cardName);
        navigate(path);
    };

    const showGoLiveButton = (userType: any) => {
        if (userType === 'Individual') {
            if (
                onboardingPercentages.personalInformation === 100 &&
                onboardingPercentages.businessInformation === 100 &&
                onboardingPercentages.bankInformation === 100 &&
                onboardingPercentages.termsAndCondition === 100
            ) {
                setShowGoLive(true);
            }
        } else if (userType === 'Company') {
            if (
                onboardingPercentages.personalInformation === 100 &&
                onboardingPercentages.businessInformation === 100 &&
                onboardingPercentages.businessCompliance === 100 &&
                onboardingPercentages.bankInformation === 100 &&
                onboardingPercentages.termsAndCondition === 100
            ) {
                setShowGoLive(true);
            }
        }
    }
    useEffect(() => {
        if (userDetails?.activeSubsidiary) {
            const { activeSubsidiary } = userDetails;
            setBusinessType(activeSubsidiary.businessType)
            setLoading(false);
            showGoLiveButton(businessType)
        }
    }, [userDetails, onboardingPercentages]);

    const isCardSelected = (name: string) => {
        let active = localStorage.getItem("activeCard")
        return active === name;
    };

    useEffect(() => {
        const fetchData = async () => {
            await getOnboardingPercentages();
        };

        fetchData();
    }, []);

    const getOnboardingPercentages = async () => {
        try {
            const response = await client.get("/subsidiary/dashboard/onboarding/percentage");
            if (response.data.status === "success") {
                // Ensure percentages are numbers and within 0-100 range
                const percentages = {
                    personalInformation: Math.min(Math.max(Number(response.data.data.personalInformation), 0), 100),
                    businessInformation: Math.min(Math.max(Number(response.data.data.businessInformation), 0), 100),
                    businessCompliance: Math.min(Math.max(Number(response.data.data.personalInformation), 0), 100),
                    bankInformation: Math.min(Math.max(Number(response.data.data.bankInformation), 0), 100),
                    termsAndCondition: Math.min(Math.max(Number(response.data.data.termsAndCondition), 0), 100),
                };
                setOnboardingPercentages(percentages);

                const stepRoutes: Record<string, string> = {
                    personalInformation: "/business/onboarding/kyc",
                    businessInformation: "/business/onboarding/kyc/business-info",
                    businessCompliance: "/business/onboarding/kyc/business-registration",
                    bankInformation: "/business/onboarding/kyc/bank-details",
                    termsAndCondition: "/business/onboarding/kyc/terms",
                };

                const cardNames: Record<string, string> = {
                    personalInformation: "Personal Info",
                    businessInformation: "business Info",
                    businessCompliance: "business Reg",
                    bankInformation: "Bank Details",
                    termsAndCondition: "Terms",
                };

                const incompleteStep = Object.keys(percentages).find(
                    key => percentages[key as keyof typeof percentages] < 100
                );

                if (incompleteStep) {
                    localStorage.setItem('activeCard', cardNames[incompleteStep]);
                    setSelectedCard(cardNames[incompleteStep]);
                    navigate(stepRoutes[incompleteStep]);
                }
            }
        } catch (err: any) {
            console.error('Error fetching onboarding percentages', err);
        }
    };
    const completeOnboarding = async () => {
        try {
            const response = await client.patch('/subsidiary/dashboard/onboarding/complete');

            // setBanks(response.data.banks);
            const message = response?.data?.message;
            showToast(message, "success")
            navigate('/overview')
        } catch (err: any) {
            if (err?.response?.data?.message === 'Please login again') {
                sessionStorage.clear();
                window.location.href = '/';
            } else {
                const message = err?.response?.data?.message;
                console.error('error getting data', err);
                showToast(message, "error")
            }
        }
    }

    const childrenWithProps = React.Children.map(children, (child) =>
        React.isValidElement(child) ? cloneElement(child as ReactElement<any>, { changeCard: setSelectedCard }) : child
    );


    return (
        <PrivateLayout>
            {loading ? <Spinner /> : (
                <div className={styles.container}>
                    <p className={styles.homeText}>Home</p>
                    <hr />
                    <Link to='/business/onboarding/'>
                        <div className='flex items-center gap-2 my-4 text-green-600 text-sm'>
                            <FaArrowLeft />
                            <p>Back to start</p>
                        </div>
                    </Link>

                    <div className={styles.gridContainer}>
                        <div>
                            <p className={styles.sectionTitle}>We need more information</p>
                            <h5 className={styles.sectionSubtitle}>
                                Your account is currently in <span className={styles.testModeText}>test mode</span>, so there are a few more things to do before you can go live and start receiving payments. Follow the steps below to get activated.
                            </h5>
                            <div className={styles.leftGrid}>
                                <Card onClick={() => handleCardClick("/business/onboarding/kyc", "Personal Info")} className={`${styles.cardContainer} ${isCardSelected("Personal Info") ? styles.selectedCardBorder : styles.unselectedCardBorder}`}>
                                    <div className={styles.cardContent}>
                                        <img src={userIcon} className={styles.userIcon} alt="User Icon" />
                                        <div className={styles.cardText}>
                                            <p>Personal Information</p>
                                            <div className='flex items-center gap-2'>

                                                <div className={styles.progressBarContainer}>
                                                    <div className={styles.progressBar} style={{ width: `${onboardingPercentages.personalInformation}%` }}></div>
                                                </div>
                                                <span >{onboardingPercentages.personalInformation}%</span>
                                            </div>
                                        </div>
                                        {isCardSelected("Personal Info") ? (
                                            <div className={styles.checkIconContainer}>
                                                <FaCheck className={styles.checkIcon} />
                                            </div>
                                        ) : <div className={styles.unselectedCardIcon}></div>
                                        }
                                    </div>
                                </Card>
                                <Card onClick={() => handleCardClick("/business/onboarding/kyc/business-info", "business Info")} className={`${styles.cardContainer} ${isCardSelected("business Info") ? styles.selectedCardBorder : styles.unselectedCardBorder}`}>
                                    <div className={styles.cardContent}>
                                        <img src={userIcon} className={styles.userIcon} alt="User Icon" />
                                        <div className={styles.cardText}>
                                            <p>Business Information</p>
                                            <div className='flex items-center gap-2'>
                                                <div className={styles.progressBarContainer}>
                                                    <div className={styles.progressBar} style={{ width: `${onboardingPercentages.businessInformation}%` }}></div>
                                                </div>
                                                <span >{onboardingPercentages.businessInformation}%</span>
                                            </div>
                                        </div>
                                        {isCardSelected("business Info") ? (
                                            <div className={styles.checkIconContainer}>
                                                <FaCheck className={styles.checkIcon} />
                                            </div>
                                        ) : <div className={styles.unselectedCardIcon}></div>
                                        }
                                    </div>
                                </Card>
                                {businessType != 'Individual' && (
                                    <Card onClick={() => handleCardClick("/business/onboarding/kyc/business-registration", "business Reg")} className={`${styles.cardContainer} ${isCardSelected("business Reg") ? styles.selectedCardBorder : styles.unselectedCardBorder}`}>
                                        <div className={styles.cardContent}>
                                            <img src={userIcon} className={styles.userIcon} alt="User Icon" />
                                            <div className={styles.cardText}>
                                                <p>Business Registration</p>
                                                <div className='flex items-center gap-2'>
                                                    <div className={styles.progressBarContainer}>
                                                        <div className={styles.progressBar} style={{ width: `${onboardingPercentages.businessCompliance}%` }}></div>
                                                    </div>
                                                    <span >{onboardingPercentages.businessCompliance}%</span>
                                                </div>
                                            </div>
                                            {isCardSelected("business Reg") ? (
                                                <div className={styles.checkIconContainer}>
                                                    <FaCheck className={styles.checkIcon} />
                                                </div>
                                            ) : <div className={styles.unselectedCardIcon}></div>
                                            }
                                        </div>
                                    </Card>)}
                                <Card onClick={() => handleCardClick("/business/onboarding/kyc/bank-details", "Bank Details")} className={`${styles.cardContainer} ${isCardSelected("Bank Details") ? styles.selectedCardBorder : styles.unselectedCardBorder}`}>
                                    <div className={styles.cardContent}>
                                        <img src={userIcon} className={styles.userIcon} alt="User Icon" />
                                        <div className={styles.cardText}>
                                            <p>Bank Details</p>
                                            <div className='flex items-center gap-2'>
                                                <div className={styles.progressBarContainer}>
                                                    <div className={styles.progressBar} style={{ width: `${onboardingPercentages.bankInformation}%` }}></div>
                                                </div>
                                                <span >{onboardingPercentages.bankInformation}%</span>
                                            </div>
                                        </div>
                                        {isCardSelected("Bank Details") ? (
                                            <div className={styles.checkIconContainer}>
                                                <FaCheck className={styles.checkIcon} />
                                            </div>
                                        ) :
                                            <div className={styles.unselectedCardIcon}></div>
                                        }
                                    </div>
                                </Card>
                                <Card onClick={() => handleCardClick("/business/onboarding/kyc/terms", "Terms")} className={`${styles.cardContainer} ${isCardSelected("Terms") ? styles.selectedCardBorder : styles.unselectedCardBorder}`}>
                                    <div className={styles.cardContent}>
                                        <img src={userIcon} className={styles.userIcon} alt="User Icon" />
                                        <div className={styles.cardText}>
                                            <p className='py-1'>Terms and Conditions</p>
                                            <div className='flex items-center gap-2'>
                                                <div className={styles.progressBarContainer}>
                                                    <div className={styles.progressBar} style={{ width: `${onboardingPercentages.termsAndCondition}%` }}></div>
                                                </div>
                                                <span >{onboardingPercentages.termsAndCondition}%</span>
                                            </div>

                                        </div>
                                        {isCardSelected("Terms") && (
                                            <div className={styles.checkIconContainer}>
                                                <FaCheck className={styles.checkIcon} />
                                            </div>
                                        )}
                                    </div>
                                </Card>

                                {showGoLive && (
                                    <div className={styles.goLiveContainer} onClick={() => { completeOnboarding() }}>
                                        <span className={styles.goLiveText}>Request to go live</span>
                                        <button className={styles.goLiveButton}>Go live now</button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.childrenContainer}>
                            {/* {children} */}
                            {childrenWithProps}
                        </div>
                    </div>
                </div>
            )}
        </PrivateLayout>
    );
};

export default Kyc;
